/**
 * Place card custom images live in Firebase Storage; templates only keep the download URL.
 * (Firestore documents are capped at 1 MiB, so inline data URLs quickly make templates unsaveable.)
 */

import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { nanoid } from 'nanoid';
import { storage } from '@/lib/firebase';
import type { PlaceCardTemplate } from '@/types';

/** Where an image is stored: under a unit, or in the shared admin (platform template) folder. */
export type PlaceCardImageScope = { kind: 'unit'; unitId: string } | { kind: 'admin' };

export const MAX_PLACE_CARD_IMAGE_BYTES = 10 * 1024 * 1024;

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
};

function storagePathFor(scope: PlaceCardImageScope, contentType: string): string {
  const extension = EXTENSION_BY_MIME[contentType] ?? 'img';
  const fileName = `${nanoid(16)}.${extension}`;
  return scope.kind === 'admin'
    ? `placeCardImages/admin/${fileName}`
    : `units/${scope.unitId}/placeCardImages/${fileName}`;
}

export async function uploadPlaceCardImage(blob: Blob, scope: PlaceCardImageScope): Promise<string> {
  const contentType = blob.type || 'application/octet-stream';
  const fileRef = storageRef(storage, storagePathFor(scope, contentType));
  await uploadBytes(fileRef, blob, { contentType });
  return getDownloadURL(fileRef);
}

export function isInlineImageUrl(url: string | undefined): url is string {
  return typeof url === 'string' && url.startsWith('data:');
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, payload = ''] = dataUrl.split(',', 2);
  const mime = /^data:([^;,]+)/.exec(header)?.[1] ?? 'application/octet-stream';
  if (header.includes(';base64')) {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }
  return new Blob([decodeURIComponent(payload)], { type: mime });
}

/**
 * Uploads any legacy inline (data URL) images to Storage and returns the template with their URLs
 * replaced, plus the element-id → URL map of what changed (empty when nothing needed migrating).
 */
export async function migrateInlinePlaceCardImages(
  template: PlaceCardTemplate,
  scope: PlaceCardImageScope
): Promise<{ template: PlaceCardTemplate; replaced: Record<string, string> }> {
  const replaced: Record<string, string> = {};
  for (const element of template.elements ?? []) {
    if (element.type === 'image' && isInlineImageUrl(element.imageUrl)) {
      replaced[element.id] = await uploadPlaceCardImage(dataUrlToBlob(element.imageUrl), scope);
    }
  }

  if (Object.keys(replaced).length === 0) {
    return { template, replaced };
  }

  return {
    template: {
      ...template,
      elements: (template.elements ?? []).map((element) =>
        replaced[element.id] ? { ...element, imageUrl: replaced[element.id] } : element
      ),
    },
    replaced,
  };
}
